/**
 * Decides whether the Space key belongs to the focused element rather than to the canvas's
 * "hold Space to pan" shortcut (`center-canvas/hooks/use-canvas-pan.ts`).
 *
 * Space is the keyboard's standard "activate" key for buttons, switches, radios, options and the
 * custom `role="button"` controls in the panels — but the pan shortcut listens on the window in
 * the capture phase, so without this it swallowed Space app-wide and those controls never saw it.
 *
 * The rule hinges on *how* the element got focus. A control reached by keyboard (Tab, arrows)
 * matches `:focus-visible`, and Space should press it, exactly like Enter. A control merely
 * clicked with the mouse does not — browsers don't apply `:focus-visible` to a mouse-clicked
 * button — and focus routinely rests on such buttons (a toolbar tool, a panel control, the Select
 * tool after a delete), so holding Space there must keep panning the canvas as it always has.
 *
 * Within that, Space goes to the element when it is an interactive control (wherever it is — the
 * on-canvas background toolbar included), or when it is any keyboard-focused element outside the
 * canvas (e.g. a tray thumbnail, which handles Space itself). Keyboard focus on the canvas surface
 * itself still pans.
 */

const INTERACTIVE_CONTROL_SELECTOR = [
  'button',
  'a[href]',
  'select',
  'summary',
  '[role="button"]',
  '[role="option"]',
  '[role="switch"]',
  '[role="radio"]',
  '[role="checkbox"]',
  '[role="tab"]',
  '[role="menuitem"]',
  '[role="menuitemradio"]',
  '[role="menuitemcheckbox"]',
  '[role="combobox"]',
].join(', ')

const CANVAS_SURFACE_SELECTOR = '[data-testid="center-canvas-scroller"]'

function isKeyboardFocusedByDefault(el: HTMLElement): boolean {
  try {
    return el.matches(':focus-visible')
  } catch {
    // An engine without `:focus-visible` support: keep the old behaviour (pan).
    return false
  }
}

export function shouldSpaceGoToFocusedElement(
  target: EventTarget | null,
  isKeyboardFocused: (el: HTMLElement) => boolean = isKeyboardFocusedByDefault
): boolean {
  if (!(target instanceof HTMLElement)) return false
  if (target === target.ownerDocument.body) return false
  if (!isKeyboardFocused(target)) return false

  if (target.closest(INTERACTIVE_CONTROL_SELECTOR)) return true

  return !target.closest(CANVAS_SURFACE_SELECTOR)
}
